import type { components } from '@fluencelabs/vodopad-admin-api'
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Switch,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'

type PromoCodeDto = components['schemas']['PromoCodeDto']

interface Props {
  open: boolean
  promoCode: PromoCodeDto | undefined
  onClose: () => void
  onUpdated: () => void
}

export function EditPromoCodeModal({
  open,
  promoCode,
  onClose,
  onUpdated,
}: Props) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (promoCode && open) {
      form.setFieldsValue({
        code: promoCode.code,
        amount: Number(promoCode.amount),
        maxTotalUsages: promoCode.maxTotalUsages,
        freeCreditsDurationDays: promoCode.freeCreditsDurationDays,
        isActive: promoCode.isActive,
        validFrom: promoCode.validFrom ? dayjs(promoCode.validFrom) : null,
        validTo: promoCode.validTo ? dayjs(promoCode.validTo) : null,
      })
    }
  }, [promoCode, open, form])

  const handleSubmit = async () => {
    if (!promoCode) return
    try {
      const values = await form.validateFields()
      setLoading(true)

      const body: components['schemas']['UpdatePromoCodeRequest'] = {
        code: values.code,
        amount: String(values.amount),
        maxTotalUsages: values.maxTotalUsages,
        freeCreditsDurationDays: values.freeCreditsDurationDays,
        isActive: values.isActive,
        validFrom: values.validFrom?.toISOString() ?? null,
        validTo: values.validTo?.toISOString() ?? null,
      }

      const result = await client.PATCH(
        '/v1/admin/promo_codes/{promo_code_id}',
        {
          params: { path: { promo_code_id: promoCode.id } },
          body,
        },
      )
      unwrapApiData(result, 'Failed to update promo code')
      void message.success('Promo code updated')
      onUpdated()
      onClose()
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={`Edit Promo Code · ${promoCode?.code ?? ''}`}
      open={open}
      onOk={() => void handleSubmit()}
      okText="Save Promo Code"
      onCancel={onClose}
      confirmLoading={loading}
      destroyOnHidden
      width={500}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="code" label="Code" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item
          name="amount"
          label="Amount ($)"
          rules={[{ required: true }]}
        >
          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="maxTotalUsages"
          label="Max Total Usages"
          rules={[{ required: true }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="freeCreditsDurationDays"
          label="Free Credits Duration (days)"
        >
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="validFrom" label="Valid From">
          <DatePicker style={{ width: '100%' }} showTime />
        </Form.Item>

        <Form.Item name="validTo" label="Valid To">
          <DatePicker style={{ width: '100%' }} showTime />
        </Form.Item>

        <Form.Item name="isActive" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
